import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Shield, MessageCircle, School, Users, ArrowRight, CheckCircle2 } from 'lucide-react';
import TrustedPartners from '../components/TrustedPartners';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Public Trust Page — live-data social proof for parents, schools, journalists,
 * partners and investors. Reachable without login at /trust.
 *
 * Reads the /api/public/trust endpoint which returns safe aggregate counts.
 */
const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'k';
  return String(n);
};

const BigNumber = ({ label, value, sub, color = '#F5831F', icon, tid }) => (
  <div
    className="bg-white rounded-2xl p-6 border flex flex-col justify-between"
    style={{ borderColor: 'rgba(245,131,31,0.15)' }}
    data-testid={tid}
  >
    <div className="flex items-center gap-2 mb-4">
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${color}1a`, color }}
      >
        {icon}
      </div>
      <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: '#6B6B6B' }}>
        {label}
      </span>
    </div>
    <div>
      <div className="text-5xl font-black leading-none tracking-tight" style={{ color: '#1A1209' }}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-2">{sub}</div>}
    </div>
  </div>
);

const TrustPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/public/trust`)
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(d => alive && setData(d))
      .catch(e => alive && setError(String(e)));
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFF7ED' }} data-testid="trust-page">
      {/* Hero */}
      <section className="relative overflow-hidden py-16 px-4" style={{ background: 'linear-gradient(135deg, #0b0d12 0%, #2a1d10 100%)' }}>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6"
               style={{ backgroundColor: 'rgba(245,131,31,0.12)', border: '1px solid rgba(245,131,31,0.3)' }}>
            <Shield size={14} style={{ color: '#F5831F' }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#F5831F' }}>Live Trust Report</span>
          </div>
          <h1 className="text-white font-black tracking-tight mb-5" style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', lineHeight: 1.05 }}>
            Every number you see on this page is <span style={{ color: '#F5831F' }}>real</span>, <span style={{ color: '#F5831F' }}>live</span>, and updated the moment it changes.
          </h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Most wellbeing platforms tell you what they <em>could</em> do. We publish what we&apos;ve <em>actually</em> done — and update it in real time.
          </p>
        </div>
      </section>

      {/* Live metrics */}
      <section className="max-w-6xl mx-auto px-4 -mt-10 relative z-20">
        {error && (
          <div className="bg-white rounded-xl p-6 border text-center text-sm text-gray-500" style={{ borderColor: '#E0E0E0' }}>
            Live data briefly unavailable — please refresh.
          </div>
        )}

        {!error && !data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0,1,2,3].map(i => <div key={i} className="bg-white rounded-2xl h-40 animate-pulse" />)}
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <BigNumber
                label="Teens & adults served"
                value={fmt(data.users)}
                sub="Real account registrations, all-time"
                color="#F5831F"
                icon={<Users size={16} />}
                tid="trust-users"
              />
              <BigNumber
                label="Schools onboarded"
                value={fmt(data.schools)}
                sub="Safeguarding dashboards issued"
                color="#7B4FD8"
                icon={<School size={16} />}
                tid="trust-schools"
              />
              <BigNumber
                label="Crisis handovers"
                value={fmt(data.crisis_handovers)}
                sub="Every one handed to a real, trained human"
                color="#DC2626"
                icon={<Heart size={16} />}
                tid="trust-crisis"
              />
              <BigNumber
                label="Conversations (30d)"
                value={fmt(data.messages_30d)}
                sub="Real dialogue between teens and AvA"
                color="#2EAA5C"
                icon={<MessageCircle size={16} />}
                tid="trust-messages"
              />
            </div>

            <div className="text-center text-xs text-gray-500 mb-12">
              Refreshed live · As of {new Date(data.as_of).toLocaleString()} · {fmt(data.waitlist)} parents on the waitlist
            </div>
          </>
        )}
      </section>

      {/* Partners */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <TrustedPartners variant="stacked" />
      </section>

      {/* Commitments */}
      <section className="bg-white py-16 px-4 border-t border-b" style={{ borderColor: '#E0E0E0' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black mb-2 tracking-tight" style={{ color: '#1A1209' }}>
            Four commitments we don&apos;t break.
          </h2>
          <p className="text-gray-600 mb-10">Not aspirations. Operating rules.</p>
          <div className="space-y-5">
            {(data?.commitments || [
              'Real-human crisis handover — no chatbot pretends to be a therapist',
              'Zero ad tracking — we don\'t sell your teen\'s attention',
              'Encrypted at rest, encrypted in transit, reviewed by safeguarding leads',
              'Every crisis event audited, evidenced, and available to schools for Ofsted',
            ]).map((c, i) => (
              <div key={i} className="flex items-start gap-4" data-testid={`trust-commitment-${i}`}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                     style={{ backgroundColor: 'rgba(46,170,92,0.12)', color: '#2EAA5C' }}>
                  <CheckCircle2 size={18} />
                </div>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: '#1A1209' }}>{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA row */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/" className="block rounded-2xl p-6 border hover:border-orange-400 transition-colors" style={{ backgroundColor: 'white', borderColor: '#E0E0E0' }} data-testid="trust-cta-parents">
            <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: '#F5831F' }}>For Parents</p>
            <h3 className="text-lg font-black mb-2" style={{ color: '#1A1209' }}>Start your teen&apos;s safest conversation</h3>
            <p className="text-sm text-gray-600 mb-4">100 free hearts. 90-second setup. Nothing to cancel.</p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: '#F5831F' }}>
              Get started <ArrowRight size={14} />
            </span>
          </Link>
          <Link to="/education" className="block rounded-2xl p-6 border hover:border-purple-400 transition-colors" style={{ backgroundColor: 'white', borderColor: '#E0E0E0' }} data-testid="trust-cta-schools">
            <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: '#7B4FD8' }}>For Schools</p>
            <h3 className="text-lg font-black mb-2" style={{ color: '#1A1209' }}>Run a free 60-day pilot</h3>
            <p className="text-sm text-gray-600 mb-4">Safeguarding analytics + friendship-growth reports, on us.</p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: '#7B4FD8' }}>
              Book a demo <ArrowRight size={14} />
            </span>
          </Link>
          <a href="mailto:business@hibff.com" className="block rounded-2xl p-6 border hover:border-gray-800 transition-colors" style={{ backgroundColor: 'white', borderColor: '#E0E0E0' }} data-testid="trust-cta-press">
            <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: '#1A1209' }}>For Press & Partners</p>
            <h3 className="text-lg font-black mb-2" style={{ color: '#1A1209' }}>Get the one-page brief</h3>
            <p className="text-sm text-gray-600 mb-4">Verified data, founder interview, press-ready assets.</p>
            <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: '#1A1209' }}>
              Email us <ArrowRight size={14} />
            </span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default TrustPage;
